import React, { FC, useMemo } from 'react';
import {
  Typography,
  Box,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  Pagination,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import moment from 'moment';
import { GetAllCategoryCommentsQuery, GetAllCommentsQuery, GuideCommentStatus } from '../__generated__/graphql';
import { Unpacked } from '../graphQLTypes/types';
import { StyledPaper } from '../theme';
import { useQuery } from '@apollo/client';
import { GET_EXTERNAL_CATEGORY_COMMENTS, GET_EXTERNAL_COMMENTS } from './comments.graphql';
import { formatDateOnly } from '../Formatters';
import { Link as RouterLink } from 'react-router-dom';
import LoadingSpinner from '../LoadingSpinner';
import { useUserContext } from '../UserContext';
import { CommentItem, firstLine, Thread, ThreadActions } from './CommentThread';
import { useGuideCommentActions } from '../guides/useGuideCommentActions';
import { useCategoryCommentActions } from '../categories/useCategoryCommentActions';

type GuideComment = NonNullable<Unpacked<NonNullable<GetAllCommentsQuery['comments']>>>;
type CategoryComment = NonNullable<Unpacked<NonNullable<GetAllCategoryCommentsQuery['categoryComments']>>>;

/** One row in the overview: customer feedback on either an anvisning or a fagområde. */
interface FeedbackRow {
  kind: 'guide' | 'category';
  comment: Thread;
  /** The short label in the first column: the guide number or the category title. */
  label: string;
  targetLabel: string;
  linkTo: string;
  linkText: string;
  ownerId?: string;
  ownerName?: string | null;
}

function guideRow(c: GuideComment): FeedbackRow {
  return {
    kind: 'guide',
    comment: c,
    label: c.guide?.docName ?? '',
    targetLabel: 'Anvisning:',
    linkTo: `/guide/${c.guide?.id}#comment-${c.id}`,
    linkText: `${c.guide?.docName ?? ''} ${c.guide?.docTitle ?? ''}`,
    ownerId: c.guide?.owner?.subjectId,
    ownerName: c.guide?.owner?.name,
  };
}

function categoryRow(c: CategoryComment): FeedbackRow {
  return {
    kind: 'category',
    comment: c,
    label: c.category?.title ?? '',
    targetLabel: 'Fagområde:',
    linkTo: `/category/${c.category?.id}#comment-${c.id}`,
    linkText: c.category?.title ?? '',
    ownerId: c.category?.owner?.subjectId,
    ownerName: c.category?.owner?.name,
  };
}

export const Overview: FC = () => {
  const guideComments = useQuery(GET_EXTERNAL_COMMENTS);
  const categoryComments = useQuery(GET_EXTERNAL_CATEGORY_COMMENTS);
  const guideActions = useGuideCommentActions();
  const categoryActions = useCategoryCommentActions();

  const rows = useMemo<FeedbackRow[]>(
    () => [
      ...(guideComments.data?.comments ?? []).filter((c): c is GuideComment => !!c).map(guideRow),
      ...(categoryComments.data?.categoryComments ?? []).filter((c): c is CategoryComment => !!c).map(categoryRow),
    ],
    [guideComments.data, categoryComments.data],
  );

  if (!guideComments.data || !categoryComments.data) {
    return <LoadingSpinner />;
  }

  return <Comments rows={rows} actionsFor={(row) => (row.kind === 'guide' ? guideActions : categoryActions)} />;
};

const Comments: FC<{ rows: FeedbackRow[]; actionsFor: (row: FeedbackRow) => ThreadActions }> = ({ rows, actionsFor }) => {
  const [selectedTab, setSelectedTab] = React.useState<'open' | 'closed'>('open');
  const showUnresolved = selectedTab === 'open';
  const [onlyMine, setOnlyMine] = React.useState(false);
  const { userId } = useUserContext();

  const visible = useMemo(() => rows.filter((row) => !onlyMine || row.ownerId === userId), [rows, onlyMine, userId]);
  const numberOfOpenThreads = useMemo(() => visible.filter((row) => row.comment.status === GuideCommentStatus.Open).length, [visible]);
  const numberOfClosedThreads = useMemo(() => visible.filter((row) => row.comment.status === GuideCommentStatus.Closed).length, [visible]);
  const filteredRows = useMemo(
    () => visible.filter((row) => row.comment.status === (showUnresolved ? GuideCommentStatus.Open : GuideCommentStatus.Closed)),
    [visible, showUnresolved],
  );

  return (
    <StyledPaper>
      <Typography variant="h5" component="h3" gutterBottom>
        Tilbakemeldinger
      </Typography>
      <FormControlLabel control={<Switch checked={onlyMine} onChange={() => setOnlyMine(!onlyMine)} />} label="Mine" />
      <Tabs value={selectedTab} onChange={(_, v) => setSelectedTab(v)}>
        <Tab label={`Åpne (${numberOfOpenThreads})`} value={'open'} />
        <Tab label={`Lukkede (${numberOfClosedThreads})`} value={'closed'} />
      </Tabs>

      {filteredRows.length === 0 && <Typography sx={{ mt: 2 }}>Ingen {showUnresolved ? 'åpne' : 'lukkede'} tilbakemeldinger</Typography>}
      <CommentList rows={filteredRows} actionsFor={actionsFor} />
    </StyledPaper>
  );
};

const PAGE_SIZE = 20;

/** Wide enough for a guide number and for most fagområde titles; longer titles ellipsize. */
const LABEL_COLUMN_WIDTH = 180;

const CommentList: FC<{ rows: FeedbackRow[]; actionsFor: (row: FeedbackRow) => ThreadActions }> = ({ rows, actionsFor }) => {
  const [selectedCommentId, setSelectedCommentId] = React.useState<string | null>(null);
  const [page, setPage] = React.useState(1);

  const sorted = useMemo(() => [...rows].sort((a, b) => moment(b.comment.createdAt).valueOf() - moment(a.comment.createdAt).valueOf()), [rows]);
  const pageCount = Math.ceil(sorted.length / PAGE_SIZE);
  const paginatedRows = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  React.useEffect(() => {
    setPage(1);
  }, [rows]);

  return (
    <Stack>
      {paginatedRows.map((row) => {
        const c = row.comment;
        return (
          <Accordion key={c.id} expanded={selectedCommentId === c.id} onChange={(_, expanded) => setSelectedCommentId(expanded ? c.id : null)}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              {/* The first column is a fixed width, not max-content: every row is its own grid, so a
                  content-sized column would put the text column in a different place on each row once
                  category titles (up to ~50 characters) sit alongside short guide numbers. */}
              <Box sx={{ display: 'grid', alignItems: 'center', gridTemplateColumns: `${LABEL_COLUMN_WIDTH}px 1fr auto`, gap: 2, width: '100%' }}>
                <Typography color="textSecondary" title={row.label} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {row.label}
                </Typography>
                <Typography sx={{ flexGrow: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{firstLine(c.text)}</Typography>
                <Typography color="textSecondary" whiteSpace="nowrap">
                  {formatDateOnly(c.createdAt)}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Stack>
                <CommentItem comment={c} selected={selectedCommentId === c.id} actions={actionsFor(row)} />
                <Box display="grid" gridTemplateColumns="max-content auto" columnGap={2} mt={2}>
                  <Typography color="textSecondary">{row.targetLabel}</Typography>
                  <Link component={RouterLink} to={row.linkTo}>
                    {row.linkText}
                  </Link>
                  <Typography color="textSecondary">Ansvarlig:</Typography>
                  <Typography>{row.ownerName}</Typography>
                </Box>
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      })}
      {pageCount > 1 && (
        <Pagination count={pageCount} page={page} onChange={(_, value) => setPage(value)} sx={{ mt: 2, display: 'flex', justifyContent: 'center' }} />
      )}
    </Stack>
  );
};
